import { useCallback, useRef, useState } from 'react';
import { sleep } from '../utils/sleep.js';
import useWatch from './use-watch.js';

export type RetryArgs<T> = {
  ready: boolean;
  readyCallback: T;
  immediateCallback?: () => void;
  retryInterval?: number;
};

/**
 * When the returned callback is invoked, invoke `readyCallback` immediately if `ready`
 * is true, otherwise wait until `ready` is true and invoke it.
 * `immediateCallback` is invoked on the first attempt, regardless of the ready state.
 * The interval that `busy` is checked can be customized with `retryIntreval`
 */
export default function useRetry<T extends (...args: any[]) => void>(args: RetryArgs<T>) {
  const { ready, readyCallback, immediateCallback, retryInterval = 10 } = args;
  const [attempt, setAttempt] = useState(0);
  const argsRef = useRef<Parameters<T>>();

  const callbackWithRetry = useCallback(
    (...args: Parameters<T>) => {
      if (immediateCallback) {
        immediateCallback();
      }

      if (ready) {
        readyCallback(...args);
      } else {
        argsRef.current = args;
        setAttempt(1);
      }
    },
    [readyCallback, immediateCallback, ready]
  );

  useWatch({
    value: attempt,
    onChange: useCallback(
      async (value: number) => {
        if (value !== 0) {
          if (ready) {
            setAttempt(0);
            readyCallback(...(argsRef.current ?? []));
          } else {
            await sleep(retryInterval);
            setAttempt(value + 1);
          }
        }
      },
      [ready, readyCallback, retryInterval]
    )
  });

  return callbackWithRetry;
}
